import React, { Fragment, useEffect, useState } from "react";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';
import { ChevronLeft, ChevronRight } from "lucide-react";

// Column Filter
const Filter = ({
  column
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '') as string}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        list={column.id + 'list'}
      />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
};
interface TableContainerProps {
  columns?: any;
  data?: any;
  tableclassName?: any;
  divclassName?: any;
  thclassName?: any;
  trclassName?: any;
  tableClass?: any;
  tdclassName?: any;
  theadclassName?: any;
  tbodyclassName?: any;
  isTfoot?: boolean;
  isSelect?: boolean;
  isBordered?: boolean;
  customPageSize?: number;
  isGlobalFilter?: boolean;
  isPagination: boolean;
  PaginationClassName?: string;
  SearchPlaceholder?: string;
}

const TableContainer = ({
  columns,
  data,
  tableclassName,
  theadclassName,
  divclassName,
  trclassName,
  thclassName,
  tdclassName,
  tbodyclassName,
  isTfoot,
  isSelect,
  isPagination,
  customPageSize,
  isGlobalFilter,
  PaginationClassName,
  SearchPlaceholder
}: TableContainerProps) => {

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  const {
    getHeaderGroups,
    getFooterGroups,
    getRowModel,
    getPageOptions,
    setPageSize,
    getState,
    getCanPreviousPage,
    getCanNextPage,
    nextPage,
    previousPage,
  } = table;

  useEffect(() => {
    Number(customPageSize) && setPageSize(Number(customPageSize));
  }, [customPageSize, setPageSize]);

  return (
    <Fragment>

      <div className="grid grid-cols-12 lg:grid-cols-12 gap-3">
        {/* {
          isSelect &&
          <div className="self-center col-span-12 lg:col-span-6">
            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-700 mr-2">Show</label>
              <select
                name="basic_tables_length"
                aria-controls="basic_tables"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                onChange={(event: any) => setPageSize(event.target.value)}
                value={getState().pagination.pageSize}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span className="text-sm font-medium text-gray-700 ml-2">entries</span>
            </div>
          </div>
        } */}

        {/* <div className="self-center col-span-12 lg:col-span-6 lg:place-self-end">
          {isGlobalFilter &&
            <div className="flex items-center">
              <label className="text-sm font-medium text-gray-700 mr-2">Search:</label>
              <DebouncedInput
                value={globalFilter ?? ''}
                onChange={value => setGlobalFilter(String(value))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                placeholder={SearchPlaceholder}
              />
            </div>
          }
        </div> */}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 max-h-[calc(100vh-420px)] sm:max-h-[calc(100vh-370px)] lg:max-h-[calc(100vh-284px)] overflow-auto">
        <div className="overflow-x-auto overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              {getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    return (
                      <th key={header.id} colSpan={header.colSpan}
                        {...{
                          className: `px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.column.getCanSort() ? 'cursor-pointer hover:bg-gray-100' : ''}`,
                          onClick: header.column.getToggleSortingHandler(),
                        }}>
                        {header.isPlaceholder ? null : (
                          <React.Fragment>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: ' ↑',
                              desc: ' ↓',
                            }
                            [header.column.getIsSorted() as string] ?? null}
                            {header.column.getCanFilter() ? (
                              <div className="mt-1">
                                <Filter column={header.column} table={table} />
                              </div>
                            ) : null}
                          </React.Fragment>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {getRowModel().rows.length > 0 ? (
                getRowModel().rows.map(row => {
                  return (
                    <tr key={row.id} className="hover:bg-gray-50 transition-colors cursor-pointer">
                      {row.getVisibleCells().map(cell => {
                        return (
                          <td key={cell.id} className="px-4 py-4 whitespace-nowrap">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 mx-auto mb-4 text-gray-400">
                        <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 mb-1">No results found</h3>
                      {/* <p className="text-gray-500">Try adjusting your search or filter to find what you're looking for.</p> */}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>

            {isTfoot && <tfoot>
              {
                getFooterGroups()?.map((footer: any, tfKey: number) => (
                  <tr key={tfKey}>
                    {
                      footer.headers?.map((tf: any, key: number) => (
                        <th key={key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-t border-gray-200">
                          {flexRender(
                            tf.column.columnDef.header,
                            tf.getContext()
                          )}
                        </th>
                      ))
                    }
                  </tr>
                ))
              }
            </tfoot>}
          </table>
        </div>
      </div>

      {
        isPagination && (
          <div className="bg-white border-t border-gray-200 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">

              {/* Left side - Page navigation */}
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={previousPage}
                  disabled={!getCanPreviousPage()}
                  className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <span className="text-sm text-gray-600 px-2 min-w-[60px] text-center">
                  {getState().pagination.pageIndex + 1} / {getPageOptions().length}
                </span>

                <button
                  onClick={nextPage}
                  disabled={!getCanNextPage()}
                  className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Center - Results info */}
              <div className="flex m-auto sm:m-0 items-center">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Total: {data.length}</span>
                </p>
              </div>

              {/* Right side - Page size options */}
              <div className="hidden sm:flex items-center justify-center sm:justify-end space-x-4">
                {[10, 25, 50].map((size) => (
                  <button
                    key={size}
                    onClick={() => setPageSize(size)}
                    className={`text-sm transition-colors px-2 py-1 rounded ${getState().pagination.pageSize === size
                      ? 'text-blue-600 font-medium bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )
      }
    </Fragment>
  );
};

export default TableContainer;